// Thin client for the same-origin /api. Never hard-codes the domain.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: { accept: 'application/json', ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message ?? `Request failed (${res.status})`, body);
  }
  return body as T;
}

export interface ServerConfig {
  env: string;
  features: { accounts: boolean; billing: boolean; microsoft?: boolean };
  licensePrice: { amount: number; currency: string };
}

export interface User {
  id: string;
  email: string;
  license: 'none' | 'lifetime';
}

export interface FeedItem {
  title: string;
  link: string | null;
  published: string | null;
  summary?: string | null;
  image?: string | null;
}

export interface CalendarEvent {
  uid: string;
  title: string;
  location: string | null;
  start: string;
  end: string;
  allDay: boolean;
  /** Set by provider calendars (Outlook) that carry their own name/colour. */
  calendar?: { id: string; name: string; color: string };
}

export interface OutlookCalendar {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  owner: string | null;
}

export interface DriveFolder {
  id: string;
  name: string;
  childCount: number;
}

export interface DriveListing {
  folder: { id: string | null; name: string; parentId: string | null };
  folders: DriveFolder[];
  mediaCount: number;
}

export interface DriveMedia {
  id: string;
  name: string;
  kind: 'image' | 'video';
  mime: string | null;
  takenAt: string | null;
}

export interface WeatherWarning {
  id: string;
  event: string;
  area: string;
  level: 'yellow' | 'orange' | 'red' | 'unknown';
  severity: string;
  onset: string | null;
  expires: string | null;
  link: string | null;
}

export interface TodoList {
  id: string;
  name: string;
  kind: string;
}

export interface TodoTask {
  id: string;
  title: string;
  done: boolean;
  important: boolean;
  createdAt: string | null;
  completedAt: string | null;
  due: string | null;
}

const todo = (listId: string, taskId?: string) =>
  `/microsoft/todo/lists/${encodeURIComponent(listId)}/tasks${taskId ? `/${encodeURIComponent(taskId)}` : ''}`;

export const endpoints = {
  todoLists: () => api<{ lists: TodoList[] }>('/microsoft/todo/lists'),
  todoTasks: (listId: string) => api<{ tasks: TodoTask[] }>(todo(listId)),
  todoAdd: (listId: string, title: string) => api<{ task: TodoTask }>(todo(listId), { method: 'POST', json: { title } }),
  todoSetDone: (listId: string, taskId: string, done: boolean) =>
    api<{ task: TodoTask }>(todo(listId, taskId), { method: 'PATCH', json: { done } }),
  todoDelete: (listId: string, taskId: string) => api<void>(todo(listId, taskId), { method: 'DELETE' }),
  warnings: (country: string, place: string) =>
    api<{ local: WeatherWarning[]; elsewhere: number }>(
      `/feeds/warnings?country=${encodeURIComponent(country)}&place=${encodeURIComponent(place)}`,
    ),
  config: () => api<ServerConfig>('/config'),
  me: () => api<{ user: User | null }>('/auth/me'),
  login: (email: string, password: string) => api<{ user: User }>('/auth/login', { method: 'POST', json: { email, password } }),
  register: (email: string, password: string) =>
    api<{ user: User }>('/auth/register', { method: 'POST', json: { email, password } }),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  getSettings: () => api<{ data: Record<string, unknown> | null; version: string | null }>('/settings'),
  putSettings: (data: Record<string, unknown>, baseVersion: string | null) =>
    api<{ version: string }>('/settings', { method: 'PUT', json: { data, baseVersion } }),
  checkout: () => api<{ url: string }>('/billing/checkout', { method: 'POST' }),
  microsoftStatus: () => api<{ connected: boolean; email: string | null; tasks?: boolean }>('/microsoft/status'),
  microsoftDisconnect: () => api<void>('/microsoft', { method: 'DELETE' }),
  driveChildren: (id?: string) =>
    api<DriveListing>(`/microsoft/drive/children${id ? `?id=${encodeURIComponent(id)}` : ''}`),
  driveMedia: (folderId: string, recursive: boolean) =>
    api<{ items: DriveMedia[]; truncated: boolean }>(
      `/microsoft/drive/media?folderId=${encodeURIComponent(folderId)}&recursive=${recursive ? 1 : 0}`,
    ),
  outlookCalendars: () => api<{ calendars: OutlookCalendar[] }>('/microsoft/calendars'),
  outlookCalendar: (days: number, hidden: string[], start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/microsoft/calendar?days=${days}&hidden=${encodeURIComponent(hidden.join(','))}${start ? `&start=${start}` : ''}`,
    ),
  pairStart: () =>
    api<{ pairingId: string; pollToken: string; code: string; expiresAt: string }>('/pair/start', { method: 'POST' }),
  pairPoll: (pairingId: string, pollToken: string) =>
    api<{ status: 'pending' | 'approved' | 'expired' }>('/pair/poll', { method: 'POST', json: { pairingId, pollToken } }),
  pairApprove: (code: string) => api<{ ok: true }>('/pair/approve', { method: 'POST', json: { code } }),
  preview: (url: string) => api<{ image: string | null }>(`/feeds/preview?url=${encodeURIComponent(url)}`),
  rss: (url: string) => api<{ title: string; items: FeedItem[] }>(`/feeds/rss?url=${encodeURIComponent(url)}`),
  ics: (url: string, days: number, start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/feeds/ics?url=${encodeURIComponent(url)}&days=${days}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}${start ? `&start=${start}` : ''}`,
    ),
};
